import { AbsoluteTimeRange } from '@chronosphereio/core';
import { ParserTerms, parser } from '@chronosphereio/editors/prometheus';
import { SyntaxNodeRef } from '@lezer/common';
import { getUnixTime } from 'date-fns';
import { MAX_DATA_POINTS_HIGH_RESOLUTION } from '@/core';
import {
  getFunctionName,
  hasIntervalVariable,
} from '../plugins/prometheus-time-series-query/promql-builder/promql-builder-model';
import { UnixTimestampSeconds } from './api-types';

const { FunctionCall } = ParserTerms;

export interface PrometheusTimeRange {
  start: UnixTimestampSeconds;
  end: UnixTimestampSeconds;
}

/**
 * Converts an AbsoluteTimeRange to Prometheus time in Unix time (i.e. in seconds).
 */
export function getPrometheusTimeRange(timeRange: AbsoluteTimeRange) {
  const { start, end } = timeRange;
  return {
    start: Math.ceil(getUnixTime(start)),
    end: Math.ceil(getUnixTime(end)),
  };
}

/**
 * Aligns a time range to a step size for Prometheus queries.
 */
export function getPrometheusAlignedTimeRange(timeRange: AbsoluteTimeRange, stepSeconds: number) {
  const { start: requestedStartTime, end: requestedEndTime } = getPrometheusTimeRange(timeRange);
  const utcOffsetSec = new Date().getTimezoneOffset() * 60;
  const alignedEnd = Math.floor((requestedEndTime + utcOffsetSec) / stepSeconds) * stepSeconds - utcOffsetSec;
  const alignedStart = Math.floor((requestedStartTime + utcOffsetSec) / stepSeconds) * stepSeconds - utcOffsetSec;
  return { start: alignedStart, end: alignedEnd };
}

/**
 * Gets the step to use for a Prom range query. Tries to take into account a suggested step size (from plugin-sytem),
 * any minimum step/resolution set by the user, and a "safe" step based on the max data points we want to allow
 * returning from a Prom query. Also considers feat flag about whether to use tenant scrape interval as minimum step.
 */
export function getRangeStep({
  timeRange,
  minStepSeconds = 15,
  resolution = 1,
  suggestedStepMs = 0,
  scrapeIntervalSeconds,
  isIncreaseWithInterval = false,
}: {
  timeRange: PrometheusTimeRange;
  minStepSeconds?: number;
  resolution?: number;
  suggestedStepMs?: number;
  scrapeIntervalSeconds?: number;
  isIncreaseWithInterval?: boolean;
}): number {
  const suggestedStepSeconds = suggestedStepMs / 1000;
  const queryRangeSeconds = timeRange.end - timeRange.start;

  let safeStepSeconds = queryRangeSeconds / MAX_DATA_POINTS_HIGH_RESOLUTION;
  if (safeStepSeconds > 1) {
    safeStepSeconds = Math.ceil(safeStepSeconds);
  }

  const stepSeconds = Math.max(suggestedStepSeconds * resolution, minStepSeconds, safeStepSeconds);
  return adjustStepForScrapeAndIncrease({ stepSeconds, scrapeIntervalSeconds, isIncreaseWithInterval });
}

/**
 * Adjust step based on scrape interval (set in envconfig for chronogateway tenantConfiguration.scrapeInterval).
 * Important to ensure we use the scrape interval as the floor for our step calculation.
 * Account for isIncreaseWithInterval to ensure the step is at least twice the scrape interval.
 * This prevents 'No data' from being returned when step is too small.
 */
export function adjustStepForScrapeAndIncrease({
  stepSeconds,
  scrapeIntervalSeconds,
  isIncreaseWithInterval = false,
}: {
  stepSeconds: number;
  scrapeIntervalSeconds?: number;
  isIncreaseWithInterval?: boolean;
}): number {
  if (typeof scrapeIntervalSeconds !== 'number') return stepSeconds;
  const stepFloor = isIncreaseWithInterval ? scrapeIntervalSeconds * 2 : scrapeIntervalSeconds;
  return Math.max(stepSeconds, stepFloor);
}

/**
 * Checks if a PromQL query uses an increase function with an $__interval variable in the range vector.
 * True when increase(<expr>[$__interval]) occurs anywhere in the query.
 * Handles multiple expressions, nesting, and sub-queries.
 */
export function queryHasIncreaseWithInterval(query?: string): boolean {
  if (!query) return false;

  // Simple check first to rule out queries that won't match
  if (!query.includes('increase') || !query.includes('$__interval')) {
    return false;
  }

  let found = false;
  const ast = parser.parse(query);
  ast.iterate({
    enter: (nodeRef: SyntaxNodeRef) => {
      if (found) return false;
      const node = nodeRef.node;
      if (node.type.is(FunctionCall)) {
        const funcName = getFunctionName(node, query);
        if (funcName === 'increase' && hasIntervalVariable(node, query)) {
          // Stop iterating when an increase function with $__interval is found
          found = true;
          return false;
        }
      }
    },
  });
  return found;
}
